
import classNames from 'classnames'
import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { Component } from 'react'

import { FormKeyContext } from '../../../lib/form-context'
import ConfirmationDeleteDialog from '../../shared/DeleteDialog/ConfirmationDeleteDialog'
import { CircularProgress } from '../../shared/NarusMaterial'

import './styles.scss'

/**
 * ## MagicGrid
 *
 * Sortable table grid for `rows` of items. This grid provides
 * its own delete confirmation modal unless you override
 * via the _handleDelete_ prop.
 *
 * AVAILABLE PROPS:
 * - **rows**          {array}     of objects to render in the grid
 * - **headers**       {array}     of objects defining the mapping of _row_ object
 *                                 keys to display names for the table grid.
 * - **canDelete**     {boolean}   to enable/disable grid-provided delete modal
 * - **onDelete**      {function}  called after the provided modal confirms deletion
 * - **handleDelete**  {function}  that overrides the provided delete modal
 * - **itemName**      {string}    for messaging when there are no items to
 *                                 display, etc
 * - **sortOn** {number} the index of field to default sort on.
 */

const fixOldHeaders = (headers = []) =>
  headers.map(h => {
    if (h.display || h.field) {
      return {
        title: h.display,
        displayFn: r => r[h.field]
      }
    }
    return h
  })

export default class MagicGrid extends Component {
  constructor (props) {
    super(props)

    this.state = {
      isInitial: true,
      sortOn: parseInt(props.sortOn, 10),
      sortDir: props.sortDir,
      expandedRows: [],
      deleteDialogOpened: false,
      deleting: null
    }

    const { headers } = props
    this.fixedHeaders = fixOldHeaders(headers)
    this.collapse = this.collapse.bind(this)
    this.handleDeleteDialogClose = this.handleDeleteDialogClose.bind(this)
    this.handleDeleteDialogSubmit = this.handleDeleteDialogSubmit.bind(this)
    this.handleDeleteClick = this.handleDeleteClick.bind(this)
    this.sortOn = this.sortOn.bind(this)
  }

  UNSAFE_componentWillMount () {
    const { sortOn } = this.state
    this.sortOn(sortOn)
  }

  handleDeleteClick (event, row) {
    this.setState({
      deleteDialogOpened: true,
      deleting: row
    })
  }

  handleDeleteDialogClose () {
    this.setState({
      deleteDialogOpened: false,
      deleting: null
    })
  }

  handleDeleteDialogSubmit (formData) {
    const { onDelete } = this.props
    const { deleting } = this.state
    if (onDelete) {
      onDelete(deleting, formData)
    }

    this.handleDeleteDialogClose()
  }

  collapse (index) {
    const { expandedRows } = this.state
    if (_.isInteger(index)) {
      this.setState({
        expandedRows: expandedRows.filter(i => i !== index)
      })
    } else {
      this.setState({
        expandedRows: []
      })
    }
  }

  toggleRow (index) {
    const { collapseRowFn } = this.props
    const { expandedRows } = this.state
    const expandedIndex = expandedRows.indexOf(index)

    if (expandedIndex === -1) {
      expandedRows.push(index)
      this.setState({ expandedRows })
    } else if (collapseRowFn) {
      collapseRowFn(index)
    } else {
      expandedRows.splice(expandedIndex, 1)
      this.setState({ expandedRows })
    }
  }

  sortOn (index = 0) {
    const { onSort } = this.props
    const { sortOn, sortDir, isInitial } = this.state

    function callback () {
      onSort?.({
        index: this.state.sortOn,
        direction: this.state.sortDir
      })
    }

    if (isInitial) {
      this.setState(
        {
          isInitial: false,
          sortOn: parseInt(sortOn, 10),
          sortDir,
          expandedRows: []
        },
        callback
      )

      return
    }

    if (index === sortOn) {
      this.setState(
        {
          sortOn,
          sortDir: sortDir === 'asc' ? 'desc' : 'asc',
          expandedRows: []
        },
        callback
      )

      return
    }

    this.setState(
      {
        sortOn: index,
        sortDir: 'asc',
        expandedRows: []
      },
      callback
    )
  }

  sortedRows () {
    const { rows } = this.props
    const sortedRows = rows ? [ ...rows ] : []
    const { sortOn, sortDir } = this.state
    let header

    if (sortOn > this.fixedHeaders.length - 1) {
      [ header ] = this.fixedHeaders
    } else {
      header = this.fixedHeaders[sortOn]
    }

    const compareValues = (a, b) => {
      let valueA
      let valueB

      valueA = a
      valueB = b

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        valueA = valueA.toUpperCase()
        valueB = valueB.toUpperCase()
      }

      if (valueA > valueB || _.isNil(valueB)) {
        return 1
      }

      if (valueA < valueB || _.isNil(valueA)) {
        return -1
      }

      return 0
    }

    const defaultSortFn = (a, b) => {
      const valueA = header?.displayFn(a)
      const valueB = header?.displayFn(b)

      return compareValues(valueA, valueB)
    }

    const makeSortByFn = attr => (a, b) => {
      const valueA = _.get(a, attr, null)
      const valueB = _.get(b, attr, null)

      return compareValues(valueA, valueB)
    }

    let sortFn = null

    if (header && header.performSort === false) {
      return rows
    }

    if (header && header.sortFn) {
      sortFn = header.sortFn
    } else if (header && header.sortBy) {
      sortFn = makeSortByFn(header.sortBy)
    } else {
      sortFn = defaultSortFn
    }

    return sortedRows.sort((a, b) => {
      const res = sortFn(a, b)

      return sortDir === 'desc' ? res * -1 : res
    })
  }

  renderHeaders () {
    const { canDelete, rows, showCounter, disableGrid } = this.props

    const mappedHeaders = this.fixedHeaders.map((header, index) => {
      if (!header.title) {
        return <th key={`accordionTableHeaderCell${index}`}>{header.title}</th>
      }

      let attrs = {}

      if (header.sortBy !== false) {
        attrs = {
          onClick: () => this.sortOn(index)
        }
      }

      return (
        <th
          key={`accordionTableHeaderCell${index}`}
          {...attrs}
          className={header.sortBy === false ? 'not-sortable' : 'sortable'}
        >
          {header.title}
          {header.sortBy !== false && this.renderArrow(index)}
        </th>
      )
    })

    if (canDelete && !disableGrid) {
      mappedHeaders.push(<th key='MagicGridHeaderSpacerCell'>&nbsp;</th>)
    }

    if (showCounter) {
      const counter = (
        <th
          id='counter-wrapper'
          key='accordionTableHeaderCount'
        >
          <div id='counter'>{(rows && rows.length) || 0}</div>
        </th>
      )
      mappedHeaders.push(counter)
    }

    return mappedHeaders
  }

  renderCells (row, rowIndex) {
    const { canDelete, rows, handleDelete, disableGrid, expansionComponent } =
      this.props
    let toggleRow = this.toggleRow.bind(this, rowIndex)

    if ((!row.component && !expansionComponent) || disableGrid) {
      toggleRow = () => null
    }

    const deleteRow = handleDelete || this.handleDeleteClick

    const tds = this.fixedHeaders.map((header, index) => (
      <td
        aria-hidden='true'
        onClick={toggleRow}
        key={`accordionCell${rowIndex}${index}`}
        colSpan={index === this.fixedHeaders.length - 1 ? 2 : 1}
      >
        {header.displayFn(row, rowIndex, rows)}
      </td>
    ))

    if (row.isSystemManaged) {
      tds.push(
        <td key={`accordionCell${rowIndex}_delete`}>
          <i className='fa fa-lock' />
        </td>
      )
    }

    if (canDelete && !row.isSystemManaged) {
      tds.push(
        <td
          aria-hidden='true'
          onClick={event => deleteRow(event, row)}
          key={`accordionCell${rowIndex}_delete`}
        >
          <i className='fa fa-trash' />
        </td>
      )
    }

    return tds
  }

  renderArrow (headerIndex) {
    const { sortOn, sortDir } = this.state
    const { igao } = this.props

    if (headerIndex === sortOn && !igao) {
      if (sortDir === 'asc') {
        return <i className='fa fa-arrow-down' />
      }
      return <i className='fa fa-arrow-up' />
    }

    return false
  }

  renderBody () {
    const sortedRows = this.sortedRows()
    const { itemName, loading, displayRowFn, igao } = this.props
    const { expandedRows } = this.state
    let loadingIndicator = null

    if (loading) {
      loadingIndicator = (
        <tr
          className='loading-indicator'
          key='loading-indicator'
        >
          <td>
            <CircularProgress />
          </td>
        </tr>
      )
    }

    if (sortedRows.length === 0) {
      return (
        <tbody>
          <tr className='data-row'>
            <td>&nbsp;</td>
            <td
              colSpan={this.fixedHeaders.length + 3}
              className='no-items'
            >
              {`There are no ${itemName} to display.`}
            </td>
          </tr>
          {loadingIndicator}
        </tbody>
      )
    }

    return sortedRows
      .map((row, index) => {
        const { expansionComponent, expansionComponentProps, disableGrid } = this.props
        const expanded = expandedRows.indexOf(index) !== -1

        let expansion = null
        const styleIgao = igao ? 'igao' : ''
        const styleClass = displayRowFn ? displayRowFn(row).join(' ') : ''
        if (expanded) {
          let expansionElement = row.component

          if (!expansionElement) {
            expansionElement = React.createElement(expansionComponent, {
              magicGridIndex: index,
              initialValues: row,
              value: row,
              row,
              ...(
                typeof expansionComponentProps === 'function'
                  ? expansionComponentProps({ index })
                  : expansionComponentProps
              )
            })
          }

          expansion = (
            <tr className='expansion'>
              <td colSpan={this.fixedHeaders.length + 3}>{expansionElement}</td>
            </tr>
          )
        }
        return (
          <tbody key={`accordionTableBody${index}`}>
            <tr className={classNames('data-row', styleClass, styleIgao, this.state.expandedRows.includes(index) && 'is-expanded')}>
              {disableGrid || igao ? null : this.renderCaret(row, index)}
              {this.renderCells(row, index)}
            </tr>

            <FormKeyContext.Provider value={index.toString(10)}>
              {expansion}
            </FormKeyContext.Provider>
          </tbody>
        )
      })
      .concat(loadingIndicator ? [ loadingIndicator ] : [])
  }

  renderCaret (row, index) {
    const { expansionComponent, disableGrid } = this.props
    if ((!row.component && !expansionComponent) || disableGrid) {
      return <td />
    }

    const { expandedRows } = this.state
    const caretClasses = classNames({
      'fa': true,
      'fa-fw': true,
      'fa-caret-right': expandedRows.indexOf(index) === -1,
      'fa-caret-down': expandedRows.indexOf(index) !== -1
    })

    const toggleRow = this.toggleRow.bind(this, index)
    return (
      <td
        onClick={toggleRow}
        className='caret'
        aria-hidden='true'
      >
        <i className={caretClasses} />
      </td>
    )
  }

  render () {
    const {
      deleteDialog,
      additionalDeleteDialogProps,
      className: propsClassName,
      loading,
      disableGrid,
      igao
    } = this.props
    const { deleteDialogOpened } = this.state
    let deleteDialogElement = null
    if (deleteDialogOpened) {
      const props = {
        handleClose: this.handleDeleteDialogClose,
        handleSubmit: this.handleDeleteDialogSubmit,
        ...additionalDeleteDialogProps
      }

      deleteDialogElement = React.createElement(deleteDialog, props)
    }

    let className = `accordionTable ${loading ? 'loading' : ''}`

    if (propsClassName) {
      className += ` ${propsClassName}`
    }

    if (this.props.hoverable) {
      className += ' hoverable'
    }

    return (
      <div>
        <table className={className}>
          <thead>
            <tr>
              {disableGrid || igao ? null : <th />}
              {this.renderHeaders()}
            </tr>
          </thead>
          {this.renderBody()}
        </table>

        {deleteDialogElement}
      </div>
    )
  }
}

MagicGrid.defaultProps = {
  igao: false,
  sortOn: 0,
  sortDir: 'asc',
  deleteDialog: ConfirmationDeleteDialog,
  additionalDeleteDialogProps: {
    prompt: 'Are you sure you want to delete this item?'
  },
  showCounter: true
}

MagicGrid.propTypes = {
  rows: PropTypes.instanceOf(Array),
  headers: PropTypes.instanceOf(Array).isRequired,
  canDelete: PropTypes.bool,
  igao: PropTypes.bool,

  deleteDialog: PropTypes.any,

  additionalDeleteDialogProps: PropTypes.any,
  onDelete: PropTypes.func,
  handleDelete: PropTypes.func,
  itemName: PropTypes.string,
  sortOn: PropTypes.number,
  sortDir: PropTypes.string,
  onSort: PropTypes.func,
  collapseRowFn: PropTypes.func,
  displayRowFn: PropTypes.func,
  showCounter: PropTypes.bool
}
